import CreateRuleView from '@modules/configuration/Rules/components/display/CreateRuleView';
import { ConfirmCreateRule } from '@modules/configuration/Rules/components/modals/ConfirmCreateRule';
import { useCreateRule } from '@modules/configuration/Rules/hooks/useCreateRule';
import { RulesModel } from '@modules/configuration/Rules/models/Rules';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@shared/components/ui/shadcn/sheet';
import { cn } from '@shared/utils/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';

type Mode = 'create' | 'edit';

export interface ClientRuleFormSheetProps {
  onClose: () => void;
  mode: Mode;
  /** Solo en mode edit */
  ruleId: number | null;
  /** Cliente hijo en /clients/:id/rules; omitir en presets (catálogo global de productos/tiendas) */
  clientTeamId?: number;
  /** Si el padre la tiene (p. ej. listado), evita GET al abrir el editor. */
  initialRule?: RulesModel;
  /**
   * Persistencia local: no llama a /shipping-rules (p. ej. borrador de preset de reglas).
   */
  localPersist?: (
    payload: Record<string, unknown>,
    context: { isEditMode: boolean; activeRuleId?: number },
  ) => Promise<void>;
}

/**
 * Crear o editar regla en panel lateral. Sustituye las rutas /rules/create y /rules/:id/edit.
 * El padre desmonta el componente al cerrar para reiniciar el formulario.
 */
export const ClientRuleFormSheet: React.FC<ClientRuleFormSheetProps> = ({
  onClose,
  mode,
  ruleId,
  clientTeamId,
  initialRule,
  localPersist,
}) => {
  const { t } = useTranslation('rulesModule');

  const form = useCreateRule({
    teamId: clientTeamId,
    successNavigateTo: '/',
    cancelNavigateTo: '/',
    editingRuleId: mode === 'edit' && ruleId != null ? ruleId : undefined,
    onFlowEnd: onClose,
    initialRule: mode === 'edit' && initialRule != null && ruleId === initialRule.id ? initialRule : undefined,
    onLocalPersist: localPersist,
  });

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        className={cn(
          'w-full sm:max-w-2xl lg:max-w-3xl flex flex-col overflow-hidden p-0 gap-0',
          'border-l border-border bg-background',
        )}
      >
        <SheetHeader className="shrink-0 space-y-1 border-b border-border bg-muted/30 px-4 py-4 pr-12 text-left sm:px-5">
          <SheetTitle className="text-base font-semibold text-foreground">
            {mode === 'edit' ? t('clientList.sheet.edit_title') : t('clientList.sheet.create_title')}
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            {t('clientList.sheet.subtitle')}
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
          <CreateRuleView
            layoutVariant="panel"
            sectionVariant="clientCard"
            isEditMode={form.isEditMode}
            loadingRule={form.loadingRule}
            loadingCondsMeta={form.loadingCondsMeta}
            ruleName={form.ruleName}
            setRuleName={form.setRuleName}
            matchAll={form.matchAll}
            setMatchAll={form.setMatchAll}
            conditions={form.conditions}
            onAddCondition={form.onAddCondition}
            onUpdateCondition={form.onUpdateCondition}
            onRemoveCondition={form.onRemoveCondition}
            conditionOptions={form.conditionOptions}
            loadOperators={form.loadOperators}
            loadValues={form.loadValues}
            loadCourierProducts={form.loadCourierProducts}
            findCourierByServiceId={form.findCourierByServiceId}
            clientTeamId={clientTeamId}
            actionType={form.actionType}
            onActionTypeChange={form.onActionTypeChange}
            courierIntegrations={form.courierIntegrations}
            courierProducts={form.courierProducts}
            loadingProducts={form.loadingProducts}
            selectedCourierIntegrationId={form.selectedCourierIntegrationId}
            onCourierChange={form.onCourierChange}
            selectedCourierProductId={form.selectedCourierProductId}
            onProductChange={form.onProductChange}
            onCancel={form.onCancel}
            onSubmit={form.onSubmit}
          />
        </div>
        <ConfirmCreateRule ref={form.confirmRef} />
      </SheetContent>
    </Sheet>
  );
};
